/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import {delayedAttrRemove} from 'panel/build/component-utils/hook-helpers';

import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {SvgIconEl} from 'common/components/svg-icon';
import {CohortsBuilderEl} from 'common/query-builder/cohorts-query-builder';
import {CustomEventModalInternalEl} from 'common/widgets/custom-event-modal';
import {CustomEventTooltipEl} from 'common/widgets/custom-event-tooltip';
import {DataGroupSelectorEl} from 'common/widgets/data-group-selector';
import {QueryBlockEl} from 'common/query-builder/query-block';
import {QueryBuilderEl} from 'common/query-builder/query-builder/panel';
import {QueryEntrySectionEl} from 'common/query-builder/query-entry-section/panel';
import {RowLabelType} from 'common/query-builder/query-entry-section/types';
import {QueryMultiBlockEl} from 'common/query-builder/query-multi-block';
import {QueryReportHeaderEl} from 'common/query-builder/query-report-header';
import {EntityType} from 'common/report/util/saved-entities/types';
import {SearchDisplayContext} from 'common/report/util/search/types';
import {MenuType} from 'common/query-builder/query-block/types';
import {QueryBlockThemeExtras} from 'common/query-builder/query-block/constants';

import {AutoDocEl} from '../components/auto-doc';
import {CodeTipEl} from '../components/code-tip';
import type {DesignSystem} from '../index';
import {Section, SubSection} from './utils';

export function QueryBuilderWidgetsSection({designSystem}: {designSystem: DesignSystem}) {
  return (
    <Section title='Query Builder Widgets'>
      <SubSection title='mp-query-builder'>
        <p sel='.desc'>A collection of query builder blocks that define a query</p>

        <CodeTipEl sel='.example.block'>
          <QueryBuilderEl
            {...{
              addEntryLabel: `Add event`,
              dateRange: designSystem.state.queryBuilderState.dateRange,
              entries: designSystem.state.queryBuilderState.events,
              filters: designSystem.state.queryBuilderState.filters,
              groupBy: designSystem.state.queryBuilderState.groupBy,
              headerTitle: `Design System Events`,
              metricsMode: true,
              projectHasCohorts: true,
              projectHasPeople: true,
              projectId: 3,
              queryTypeOptions: [
                {value: `multi`, icon: `community`, label: ``, tooltipLabel: `example tooltip`},
                {value: `refresh`, icon: `refresh`, label: ``, tooltipLabel: `a random icon`},
              ],
              selectedQueryType: `multi`,
              showEntryRename: true,
              showSessions: true,
              trackName: `design-system`,
              userId: 0,
              bodySection: `Slotted query builder section`,
            }}
            onUpdate={(detail) => designSystem.update({queryBuilderState: detail})}
          />
        </CodeTipEl>
      </SubSection>
      <SubSection title='mp-query-report-header' autoDoc={true}>
        <p sel='.desc'>A header area that provides interactions for saved reports</p>
        <CodeTipEl sel='.example.block'>
          <QueryReportHeaderEl
            params={{
              entityType: EntityType.Report,
              canSave: true,
              controlItems: [{slot: `menu-item`}],
              projectId: 3,
              allowStaffOverride: false,
              canShare: false,
              canWrite: true,
              displayContext: SearchDisplayContext.Dashboard,
              hasUnsavedChanges: false,
              invalidEntity: false,
              invalidMessage: `invalid`,
              isSuperadmin: false,
              overflowControlItems: [],
              selectedEntityDescription: `Description`,
              selectedEntityId: null,
              selectedEntityName: null,
              trackName: `dashboard`,
              userId: 1,
              workspaceId: 1,
            }}
          >
            <div attrs={{slot: `title-subtext`}}>Slotted Subtitle</div>
            <ButtonEl
              attrs={{
                icon: `circle-checkmark`,
                slot: `menu-item`,
                theme: ButtonTheme.Tertiary,
              }}
            >
              Slotted Menu Item
            </ButtonEl>
          </QueryReportHeaderEl>
        </CodeTipEl>
      </SubSection>
      <SubSection title='mp-cohorts-query-builder'>
        <p sel='.desc'>The query builder from the Cohorts report. Will also be used in Explore and Messages.</p>
        <CodeTipEl sel='.example.block'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary}}
            on={{
              click: () =>
                !designSystem.state.open.cohortsQueryBuilder
                  ? designSystem.helpers.openModal(`cohortsQueryBuilder`)
                  : designSystem.helpers.closeModal(`cohortsQueryBuilder`),
            }}
          >
            Show the cohorts query builder
          </ButtonEl>
          {designSystem.state.open.cohortsQueryBuilder && <CohortsBuilderEl projectId={3} nonCollapsible={true} />}
        </CodeTipEl>
      </SubSection>
      <SubSection title='mp-data-group-selector' autoDoc={true}>
        <CodeTipEl sel='.example.block'>
          <DataGroupSelectorEl
            attrs={{
              'project-id': 3,
              'selected-data-group-id': designSystem.state.dataGroupID,
            }}
            on={{selectDataGroup: designSystem.helpers.selectDataGroup}}
            props={{dataGroups: designSystem.helpers.getDataGroups()}}
          />
        </CodeTipEl>
      </SubSection>
      <SubSection title='mp-query-block' autoDoc={true}>
        <p sel='.desc'>
          Query block is a basic building block of a query builder that allows selection of entities e.g. events,
          cohorts, properties.
        </p>
        <CodeTipEl sel='.example'>
          <QueryBlockEl
            attrs={{
              type: MenuType.Events,
              'data-key': `event`,
              'header-text': `Event`,
              label: `Select Event`,
              'project-id': 3,
              selected: designSystem.state.queryBlockSelectedEvent,
              theme: QueryBlockThemeExtras.Builder,
            }}
            on={{
              select: (ev: CustomEvent) => designSystem.update({queryBlockSelectedEvent: ev.detail.item}),
            }}
          />
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <QueryBlockEl
            attrs={{
              type: MenuType.Time,
              'data-key': `time`,
              selected: designSystem.state.queryBlockSelectedTime,
              theme: QueryBlockThemeExtras.Builder,
              'project-id': 3,
            }}
            on={{
              select: (ev: CustomEvent) => designSystem.update({queryBlockSelectedTime: ev.detail}),
            }}
          />
        </CodeTipEl>
        <CodeTipEl sel='.example'>
          <QueryBlockEl
            attrs={{
              type: MenuType.Filter,
              'data-key': `event-filter`,
              'dropdown-alignment': `left`,
              'event-names': `["Viewed report"]`,
              'prefix-label': `where`,
              'project-has-cohorts': true,
              'project-has-people': true,
              'project-id': 3,
              selected: designSystem.state.queryBlockSelectedFilter,
              'selector-icon': `filter`,
              theme: QueryBlockThemeExtras.Builder,
            }}
            on={{
              select: (ev: CustomEvent) => designSystem.update({queryBlockSelectedFilter: ev.detail.item}),
            }}
          />
        </CodeTipEl>
        <br />
        <CodeTipEl sel='.example'>
          <QueryBlockEl
            attrs={{
              type: MenuType.Properties,
              'all-data-groups': true,
              'data-key': `property`,
              'project-has-cohorts': true,
              'project-has-people': true,
              'project-id': 3,
              selected: designSystem.state.queryBlockSelectedProperty,
              'user-id': 1,
              theme: QueryBlockThemeExtras.Builder,
            }}
            on={{
              select: (ev: CustomEvent) => designSystem.update({queryBlockSelectedProperty: ev.detail.item}),
            }}
          />
        </CodeTipEl>
      </SubSection>
      <SubSection title='mp-query-multi-block' autoDoc={true}>
        <CodeTipEl sel='.example'>
          <QueryMultiBlockEl
            attrs={{
              type: `filter`,
              'project-id': 3,
              'button-icon': `filter`,
              'button-text': `Add Filter`,
              'project-has-people': `true`,
              selected: designSystem.state.queryMultiBlockSelectedFilters,
              theme: `secondary`,
              'data-key': `property-filters`,
            }}
            on={{
              update: (ev) => designSystem.update({queryMultiBlockSelectedFilters: ev.detail.items}),
            }}
          />
        </CodeTipEl>
      </SubSection>
      <SubSection title='mp-query-entry-section'>
        <CodeTipEl sel='.example.block'>
          <QueryEntrySectionEl
            addEntryLabel='Add event'
            entries={designSystem.state.queryEntrySectionEntries}
            projectHasPeople
            projectId={3}
            rowDraggable
            rowLabelType={RowLabelType.Numeric}
            onUpdate={(updates) => designSystem.update({queryEntrySectionEntries: updates.entries})}
          />
        </CodeTipEl>
      </SubSection>
      <SubSection title='mp-custom-event-modal' autoDoc={true}>
        <p sel='.desc'>
          Custom event modal allows a user to add/edit/delete a custom event. <br />
          NOTE: There is a bug that puts the modal at top of the page rather than top of the viewport.
        </p>

        <CodeTipEl sel='.example'>
          <div sel='.editable-custom-event'>
            <span sel='.custom-event-icon'>
              <SvgIconEl attrs={{icon: `event`}} />
            </span>
            <span sel='.edit-label'>{designSystem.state.customEvent.name}</span>
            <span sel='.edit-custom-event-icon' on={{click: designSystem.helpers.clickEditCustomEvent}}>
              <SvgIconEl attrs={{icon: `edit`}} />
            </span>
            <CustomEventTooltipEl
              attrs={{'custom-event-id': designSystem.state.customEvent.id}}
              props={{customEvents: designSystem.helpers.getEvents().filter((ev) => ev.custom)}}
            />
          </div>

          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary, icon: `plus`}}
            on={{click: designSystem.helpers.createNewCustomEvent}}
          >
            Custom Event
          </ButtonEl>

          {designSystem.state.stagedCustomEvent && (
            <CustomEventModalInternalEl
              hook={{remove: delayedAttrRemove(`open`, false)}}
              attrs={designSystem.helpers.filterAttributes({
                open: true,
                events: JSON.stringify(designSystem.helpers.getEvents()),
                'is-blocked-people': false,
                'project-has-people': true,
                'project-id': 4,
                'selected-custom-event-id': designSystem.state.stagedCustomEvent?.id,
              })}
              on={{
                modalClosed: designSystem.helpers.customEventsModalClosed,
                deletedCustomEvent: designSystem.helpers.customEventsModalClosed,
                savedCustomEvent: designSystem.helpers.savedCustomEvent,
              }}
            />
          )}
        </CodeTipEl>

        <AutoDocEl attrs={{component: `mp-custom-event-tooltip`}} />
      </SubSection>
    </Section>
  );
}
